import { Component, ChangeDetectorRef, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { LoginOtpService } from './login-otp.service';
import { Router } from '@angular/router';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';
import { MasterDataService } from 'src/app/core/service/master-data.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ApplicationConfigService } from 'src/app/core/config/application-config.service';

@Component({
  selector: 'app-login-otp',
  templateUrl: './login-otp.component.html',
  styleUrls: ['./login-otp.component.scss'],
  standalone: false
})
export class LoginOtpComponent implements OnInit {
  currentStep: 'email' | 'otp' = 'email';
  email: string = '';
  errorMessage: string = '';
  successMessage: string = '';
  isLoading: boolean = false;
  isResendDisabled: boolean = false;
  resendCountdown: number = 30;

  emailForm: FormGroup;
  otpForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private authService: LoginOtpService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private localStorage: LocalStorageService,
    private sessionStorage: SessionStorageService,
    private masterDataService: MasterDataService,
    private http: HttpClient,
    private applicationConfigService: ApplicationConfigService
  ) {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, this.emailOrMobileValidator]]
    });

    this.otpForm = this.fb.group({
      otp: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    // Clear previous auth tokens (similar to SigninComponent)
    this.localStorage.clear('authenticationToken');
    this.sessionStorage.clear('authenticationToken');
  }

  // Step 1: Send OTP using an email address or mobile number.
  // The control and API field remain named `email` to preserve the backend contract.
  onSubmitEmail(): void {
    if (this.emailForm.valid) {
      this.isLoading = true;
      const emailToSend = this.emailForm.value.email.trim();
      if (!emailToSend) {
        this.setMessage('Please enter a valid email address or mobile number.', true);
        this.isLoading = false;
        return;
      }

      this.authService.sendOtp(emailToSend).subscribe({
        next: () => {
          this.email = emailToSend;
          this.configureOtpValidation();
          this.setMessage('OTP sent successfully!', false);
          this.currentStep = 'otp';
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          // err is an Error object from service.handleError -> Error.message
          const msg = err?.message || 'Failed to send OTP. Please try again.';
          this.setMessage(msg, true);
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      });
    } else {
      this.emailForm.markAllAsTouched();
      this.setMessage('Please enter a valid email address or 10-digit mobile number.', true);
    }
  }

  // Step 2: Verify OTP
  onSubmitOtp(): void {
    if (this.otpForm.valid) {
      this.isLoading = true;
      const otp = this.otpForm.value.otp.trim();

      this.authService.verifyOtp(this.email.trim(), otp).subscribe({
        next: async (response) => {
          this.isLoading = false;

          // Debug
          console.log('verifyOtp response:', response);

          // Backends vary — support both {otpVerified: true, access_token: '...'} or direct token
          const verified = response?.otpVerified === true || !!response?.access_token || !!response?.token;
          const accessToken = response?.access_token ?? response?.token ?? response?.accessToken;

          if (verified && accessToken) {
            // store token & user info
            this.localStorage.store('authenticationToken', accessToken);
            const emailFromResp = response?.email ?? this.email;
            this.localStorage.store('email', emailFromResp);
            if (response?.group) {
              this.localStorage.store('userGroup', response.group);
            }

            this.authService.loginWithOtpSuccess(accessToken, emailFromResp, response?.group);

            // fetch master-data (same as SigninComponent) so app knows who this email belongs to
            try {
              const masterDataUrl = this.applicationConfigService.getJpaMicroServiceEndpointFor
                ? this.applicationConfigService.getJpaMicroServiceEndpointFor('api/login/master-data')
                : this.applicationConfigService.getEndpointFor
                ? `${this.applicationConfigService.getEndpointFor('api')}/login/master-data`
                : '/api/login/master-data';

              const headers: HttpHeaders = new HttpHeaders({ Authorization: accessToken });
              this.http.get(masterDataUrl, { headers }).subscribe({
                next: (masterData) => {
                  this.masterDataService.setMasterData(masterData);
                  this.setMessage('OTP verified successfully! Redirecting...', false, 1200);
                  setTimeout(() => this.router.navigate(['home']), 1200);
                },
                error: (mdErr) => {
                  // master-data failed — still proceed to home but notify
                  console.warn('Failed to fetch master-data after OTP login', mdErr);
                  this.setMessage('OTP verified successfully! Redirecting...', false, 800);
                  setTimeout(() => this.router.navigate(['home']), 800);
                }
              });
            } catch (e) {
              console.warn('master-data fetch skipped due to config issue', e);
              this.setMessage('OTP verified successfully! Redirecting...', false, 800);
              setTimeout(() => this.router.navigate(['home']), 800);
            }
          } else {
            this.setMessage('Invalid OTP. Please try again.', true);
          }
        },
        error: (err: any) => {
          this.isLoading = false;
          console.error('OTP verification error:', err);

          // The service throws Error objects; map known backend exceptions to friendly messages
          const raw = err?.message ?? '';
          if (raw.includes('Index 0 out of bounds') || raw.toLowerCase().includes('indexoutofbound')) {
            this.setMessage('The entered email address or mobile number is not valid.', true);
          } else if (raw.toLowerCase().includes('invalid otp') || raw.toLowerCase().includes('otp invalid')) {
            this.setMessage('Invalid OTP. Please check and try again.', true);
          } else {
            this.setMessage(raw || 'Failed to verify OTP. Please try again.', true);
          }
        }
      });
    } else {
      this.setMessage(`Please enter a ${this.otpLength}-digit OTP.`, true);
    }
  }

  // Resend OTP logic
  resendOtp(): void {
    if (this.isResendDisabled) return;

    if (!this.email || !this.email.trim()) {
      this.setMessage('Email or mobile number is empty. Please enter it first.', true);
      return;
    }

    this.isResendDisabled = true;
    this.resendCountdown = 30;

    const countdownInterval = setInterval(() => {
      this.resendCountdown--;
      if (this.resendCountdown <= 0) {
        this.isResendDisabled = false;
        clearInterval(countdownInterval);
        this.cdr.detectChanges();
      } else {
        this.cdr.detectChanges();
      }
    }, 1000);

    this.authService.sendOtp(this.email).subscribe({
      next: () => {
        this.setMessage('OTP resent successfully!', false);
      },
      error: (err) => {
        this.setMessage(err?.message || 'Failed to resend OTP. Please try again.', true);
      }
    });
  }

  private emailOrMobileValidator(control: AbstractControl): ValidationErrors | null {
    const value = String(control.value ?? '').trim();
    if (!value) return null;

    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    const isMobile = /^\d{10}$/.test(value);
    return isEmail || isMobile ? null : { emailOrMobile: true };
  }

  get otpLength(): number {
    return /^\d{10}$/.test(this.email.trim()) ? 4 : 6;
  }

  private configureOtpValidation(): void {
    const otpControl = this.otpForm.get('otp');
    otpControl?.setValidators([
      Validators.required,
      Validators.pattern(`^[0-9]{${this.otpLength}}$`)
    ]);
    otpControl?.reset();
    otpControl?.updateValueAndValidity();
  }

  // Helper to show success / error messages
  private setMessage(message: string, isError: boolean = true, timeout: number = 5000): void {
    if (isError) {
      this.errorMessage = message;
      this.successMessage = '';
    } else {
      this.successMessage = message;
      this.errorMessage = '';
    }

    if (timeout > 0) {
      setTimeout(() => {
        if (this.errorMessage === message || this.successMessage === message) {
          this.errorMessage = '';
          this.successMessage = '';
          this.cdr.detectChanges();
        }
      }, timeout);
    }
    this.cdr.detectChanges();
  }

  /**
   * Suggested helper to upload an image (you mentioned upload image problem).
   * Use this pattern for file uploads (multipart/form-data). Adjust endpoint path to your backend.
   * If you already have an UploadService, move the logic there.
   */
  uploadImage(file: File): void {
    if (!file) {
      this.setMessage('No file selected for upload.', true);
      return;
    }

    const tenantId = this.localStorage.retrieve('tenantId');
    const form = new FormData();
    form.append('file', file, file.name);

    const headers: any = {}; // do NOT set Content-Type; browser will set boundary
    if (tenantId) headers['X-Tenant-ID'] = tenantId;
    // if your backend requires token header:
    const token = this.localStorage.retrieve('authenticationToken');
    if (token) headers['Authorization'] = token;

    this.isLoading = true;
    this.http.post(this.applicationConfigService.getJpaMicroServiceEndpointFor
      ? this.applicationConfigService.getJpaMicroServiceEndpointFor('api/upload')
      : `${this.applicationConfigService.getEndpointFor('api')}/upload`, form, { headers })
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          this.setMessage('Image uploaded successfully.', false);
        },
        error: (err) => {
          this.isLoading = false;
          console.error('Image upload failed', err);
          const raw = err?.error?.message || err?.message || '';
          if (raw.toLowerCase().includes('file too large')) {
            this.setMessage('File is too large. Please upload a smaller file.', true);
          } else {
            this.setMessage('Image upload failed. Please try again.', true);
          }
        }
      });
  }
}
